import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../utils/AxiosHelper';
import API from '../../modules/settings/APIs';
import { useAppSelector } from '../hooks';

export interface EmailServerConfig {
  from: string;
  fromName?: string | null;
  replyTo?: string | null;
  replyToName?: string | null;
  envelopeFrom?: string | null;
  host: string;
  port?: string | null;
  username?: string | null;
  /** Write-only: sent on save/test, never returned by the backend. */
  password?: string;
  ssl: boolean;
  starttls: boolean;
  auth: boolean;
  debug: boolean;
  /** Read-only: whether a password is stored for this tenant. */
  passwordSet?: boolean;
}

// SMTP handshakes can be slow (backend waits up to 30s); the default
// apiRequest timeout of 10s would abort a test that is still in progress.
const TEST_TIMEOUT_MS = 45000;

const queryKey = ['settings', 'emailServer'] as const;

export const useEmailServerConfig = () => {
  const { isAuthenticated, token } = useAppSelector((state) => state.session);

  return useQuery({
    queryKey,
    queryFn: async (): Promise<EmailServerConfig | null> => {
      const response = await apiRequest.get(API.settings.emailServer.get);
      return response.data?.data ?? null;
    },
    enabled: isAuthenticated && !!token,
  });
};

export const useSaveEmailServerConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: EmailServerConfig): Promise<EmailServerConfig | null> => {
      const response = await apiRequest.post(API.settings.emailServer.save, config);
      return response.data?.data ?? null;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
    },
  });
};

export const useTestEmailServerConfig = () => {
  return useMutation({
    mutationFn: async (config: EmailServerConfig): Promise<void> => {
      await apiRequest.post(API.settings.emailServer.test, config, {
        timeout: TEST_TIMEOUT_MS,
      });
    },
  });
};
